import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "../../css/ViewPostedJobs.module.css";

const ViewPostedJobs = () => {
  const [postedJobs, setPostedJobs] = useState([]);
  const [repostedJobs, setRepostedJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [requirements, setRequirements] = useState("");
  const [jobType, setJobType] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchPostedAndRepostedJobs();
  }, []);

  const fetchPostedAndRepostedJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        setError("You need to be logged in to view jobs.");
        setLoading(false);
        return;
      }

      const queryParams = new URLSearchParams();
      if (searchTerm) queryParams.append("title", searchTerm);
      if (company) queryParams.append("company", company);
      if (location) queryParams.append("location", location);
      if (requirements) queryParams.append("requirements", requirements);
      if (jobType) queryParams.append("jobType", jobType);

      const response = await axios.get(
        `https://techservicemarket-backend.onrender.com/api/university/view-posted-jobs?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const posted = response.data.filter((job) => job.universityPartnerId && job.originalJobId === null);
      const reposted = response.data.filter((job) => job.originalJobId);

      setPostedJobs(posted);
      setRepostedJobs(reposted);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching posted and reposted jobs:", error);
      setError("Failed to fetch jobs. Please try again later.");
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this job? This action cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("You need to be logged in to delete jobs.");
        return;
      }

      await axios.delete(
        `https://techservicemarket-backend.onrender.com/api/university/jobs/${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert("Job deleted successfully.");
      fetchPostedAndRepostedJobs();
    } catch (error) {
      console.error("Error deleting job:", error);
      alert("Failed to delete job. Please try again later.");
    }
  };

  if (loading) {
    return <div className={styles.dashboardContainer}>Loading...</div>;
  }

  return (
    <div className={styles.dashboardContainer}>
      <button
        onClick={() => navigate("/university/dashboard")}
        className={styles.backButton}
      >
        ← Back to Dashboard
      </button>
      <h1 className={styles.dashboardHeader}>Your Posted and Reposted Jobs</h1>

      {/* Search Section */}
      <div className={styles.searchSection}>
        <input
          type="text"
          placeholder="Search by Title"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
        <input
          type="text"
          placeholder="Search by Company"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className={styles.searchInput}
        />
        <input
          type="text"
          placeholder="Search by Location"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className={styles.searchInput}
        />
        <input
          type="text"
          placeholder="Requirements (comma-separated)"
          value={requirements}
          onChange={(e) => setRequirements(e.target.value)}
          className={styles.searchInput}
        />
        <select
          value={jobType}
          onChange={(e) => setJobType(e.target.value)}
          className={styles.selectInput}
        >
          <option value="">Select Job Type</option>
          <option value="Full-time">Full-time</option>
          <option value="Part-time">Part-time</option>
          <option value="Contract">Contract</option>
          <option value="Internship">Internship</option>
        </select>
        <button onClick={fetchPostedAndRepostedJobs} className={styles.searchButton}>
          Search
        </button>
      </div>

      <div
        className={styles.jobSectionsContainer}
        style={{ display: "flex", gap: "20px", marginTop: "20px" }}
      >
        {/* Posted Jobs Section */}
        <div className={styles.jobSection} style={{ flex: 1 }}>
          <h2>Posted Jobs</h2>
          {postedJobs.length === 0 ? (
            <p>No posted jobs found. Start by posting a job.</p>
          ) : (
            <div className={styles.jobListSection}>
              {postedJobs.map((job) => (
                <div key={job._id} className={styles.jobCard}>
                  <h3>{job.title}</h3>
                  <p>
                    <strong>Company:</strong> {job.company}
                  </p>
                  <p>
                    <strong>Location:</strong> {job.location}
                  </p>
                  <p>
                    <strong>Job Type:</strong> {job.jobType}
                  </p>
                  <p>
                    <strong>Requirements:</strong>{" "}
                    {job.requirements.join(", ")}
                  </p>
                  <p>
                    <strong>Description:</strong> {job.description}
                  </p>
                  <p>
                    <strong>Salary:</strong>{" "}
                    {job.salary ? `$${job.salary}` : "Not specified"}
                  </p>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={() =>
                        navigate(`/university/jobs/${job._id}/edit`)
                      }
                      className={styles.editButton}
                      style={{ backgroundColor: "#ffc107" }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteJob(job._id)}
                      className={styles.deleteButton}
                      style={{ backgroundColor: "#dc3545", color: "#fff" }}
                    >
                      Delete
                    </button>
                    <button
                      onClick={() =>
                        navigate(`/university/jobs/${job._id}/applications`)
                      }
                      className={styles.viewButton}
                      style={{ backgroundColor: "#17a2b8", color: "#fff" }}
                    >
                      View Applications
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reposted Jobs Section */}
        <div className={styles.jobSection} style={{ flex: 1 }}>
          <h2>Reposted Jobs</h2>
          {repostedJobs.length === 0 ? (
            <p>No reposted jobs found. Repost jobs from the dashboard.</p>
          ) : (
            <div className={styles.jobListSection}>
              {repostedJobs.map((job) => (
                <div key={job._id} className={styles.jobCard}>
                  <h3>{job.title}</h3>
                  <p>
                    <strong>Company:</strong> {job.company}
                  </p>
                  <p>
                    <strong>Location:</strong> {job.location}
                  </p>
                  <p>
                    <strong>Job Type:</strong> {job.jobType}
                  </p>
                  <p>
                    <strong>Requirements:</strong>{" "}
                    {job.requirements.join(", ")}
                  </p>
                  <p>
                    <strong>Description:</strong> {job.description}
                  </p>
                  <p>
                    <strong>Salary:</strong>{" "}
                    {job.salary ? `$${job.salary}` : "Not specified"}
                  </p>
                  <button
                    onClick={() => handleDeleteJob(job._id)}
                    className={styles.deleteButton}
                    style={{ backgroundColor: "#dc3545", color: "#fff" }}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewPostedJobs;
